import { useEffect, useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import {
  abortBrowserDownload,
  cancelResumable,
  getDownloadState,
  onDownloadStateChange,
  refreshResumable,
  resumeBrowserDownload,
  type DownloadState,
} from "./controller";

/**
 * Live UI for the browser downloader. Two shapes:
 *
 * **Running** — a full-screen overlay that blocks the entire site. The download is driven by
 * this tab: navigating away, or clicking something that re-renders the cart, would abandon
 * every fetch in flight and lose the chunk being assembled. Rather than trying to guess which
 * controls are dangerous, nothing is reachable except Stop. Only this downloader locks the
 * page — the Lambda path builds the archive server-side and is unaffected by what the user
 * does, so its UI is untouched.
 *
 * **Idle with an unfinished job** — an in-cart prompt offering Resume or Cancel.
 *
 * Subscribes to the module-level store rather than owning state, so the cart re-rendering
 * (which it does on every refresh) cannot reset a download in flight.
 */
/**
 * Wraps the download cart: renders the downloader's own UI above it, and disables everything
 * below while a download is running OR left unfinished.
 *
 * An unfinished job blocks too, not just an active one. The reseller has a decision to make —
 * resume or discard — and letting them build a new cart alongside a half-finished download
 * would either start a second job the server refuses, or quietly strand the first. One
 * decision at a time.
 *
 * `inert` rather than only opacity: a faded control that still takes clicks and keyboard
 * focus is worse than one that plainly cannot be used.
 */
export function BrowserDownloadGate({ children }: { children: preact.ComponentChildren }) {
  const [s, setS] = useState<DownloadState>(getDownloadState());
  useEffect(() => onDownloadStateChange(setS), []);
  const blocked = s.running || s.resumable !== null;

  return (
    <>
      <BrowserDownloadPanel />
      <div class={blocked ? "browserdl-blocked" : undefined} {...(blocked ? { inert: true } : {})}>
        {children}
      </div>
    </>
  );
}

export function BrowserDownloadPanel() {
  const [s, setS] = useState<DownloadState>(getDownloadState());

  useEffect(() => {
    const off = onDownloadStateChange(setS);
    void refreshResumable();
    return off;
  }, []);

  useEffect(() => {
    if (!s.running) return;
    // Belt and braces alongside the overlay: the overlay stops clicks, this catches a reload,
    // a typed URL or a closed tab. The browser shows its own generic wording.
    const warn = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    // The page behind the overlay must not scroll — a locked page that still scrolls reads as
    // half-broken rather than deliberately held.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("beforeunload", warn);
      document.body.style.overflow = previousOverflow;
    };
  }, [s.running]);

  // Portalled to <body>. `div.download-cart` sets `backdrop-filter: blur(20px)`, and a
  // filter makes an element the containing block for `position: fixed` descendants — so
  // rendered in place the overlay covered only the 350px cart box, leaving the rest of the
  // site clickable. This is the bug, not the z-index.
  if (s.running) return createPortal(<RunningOverlay s={s} />, document.body);

  if (s.error) {
    return (
      <div class="browserdl-panel">
        <div class="browserdl-title">
          <i class="fas fa-triangle-exclamation" /> Download failed
        </div>
        <div class="browserdl-sub browserdl-error">{s.error}</div>
      </div>
    );
  }

  if (s.resumable) {
    const { completed, total } = s.resumable;
    return (
      <div class="browserdl-panel">
        <div class="browserdl-title">
          <i class="fas fa-rotate-left" /> Unfinished download
        </div>
        <div class="browserdl-sub">
          {completed} of {total} artworks were downloaded. The rest are still waiting.
        </div>
        <div class="browserdl-actions">
          <span
            class="button medium green"
            onClick={() => {
              void resumeBrowserDownload();
            }}
          >
            <i class="fas fa-play" /> Resume — {total - completed} left
          </span>
          <span
            class="button medium grey"
            onClick={() => {
              if (confirm("Discard this unfinished download? The artworks already downloaded are kept.")) {
                void cancelResumable();
              }
            }}
          >
            <i class="fas fa-times" /> Cancel
          </span>
        </div>
      </div>
    );
  }

  return null;
}

/** The blocking overlay shown while files are being fetched. */
function RunningOverlay({ s }: { s: DownloadState }) {
  // Progress across the whole job, blending finished chunks with the current chunk's files.
  //
  // The bar used to read `artworksDone / artworksTotal`, which only advances when a chunk is
  // REPORTED — so a single-chunk download sat at 0% for its entire duration and then jumped
  // to 100%. Counting the chunk in flight is what makes it move while files are arriving.
  const chunkFraction = s.filesTotal > 0 ? s.filesDone / s.filesTotal : 0;
  const overall =
    s.chunkCount > 0 ? (Math.max(0, s.chunkIndex - 1) + chunkFraction) / s.chunkCount : 0;
  const jobPct = Math.min(100, Math.round(overall * 100));

  return (
    <div class="browserdl-lock" id="browserdl-lock">
      <div class="browserdl-lock-box">
        <div class="browserdl-title">
          <i class="fas fa-download" /> Downloading — {s.artworksDone} of {s.artworksTotal} artworks
        </div>

        <div class="browserdl-bar">
          <div class="browserdl-bar-fill" style={{ width: `${jobPct}%` }} />
        </div>

        <div class="browserdl-sub">
          Part {s.chunkIndex} of {s.chunkCount} — {s.filesDone}/{s.filesTotal} files in this zip
          {s.artworksTotal > 0 ? ` · ${s.artworksDone}/${s.artworksTotal} artworks saved` : ""}
        </div>
        <div class="browserdl-sub browserdl-hint">
          Keep this tab open. Each part arrives as its own zip file. You can stop at any time —
          finished parts are kept and the rest can be resumed later.
        </div>

        {s.failures.length > 0 && (
          <div class="browserdl-sub browserdl-error">
            {s.failures.length} file(s) could not be fetched and were left out:{" "}
            {s.failures.slice(0, 5).join(", ")}
            {s.failures.length > 5 ? ` and ${s.failures.length - 5} more` : ""}
          </div>
        )}

        <div class="browserdl-actions">
          <span
            class="button medium red"
            onClick={() => {
              abortBrowserDownload();
            }}
          >
            <i class="fas fa-stop" /> Stop
          </span>
        </div>
      </div>
    </div>
  );
}
